import CountUpNumber from "../CountUpNumber";
import useIsMobile from "../../../../../hooks/useIsMobile";
import { useEffect, useState } from "react";
import { endOfDay, format } from "date-fns";
import { Skeleton } from "primereact/skeleton";
import { AnimatePresence, motion } from "framer-motion";

const OwnerDashboardGeneralOverview = (props) => {
  const { data, filterValue, loading } = props;

  const isMobile = useIsMobile(1000);
  const [totalRevenue, setTotalRevenue] = useState(0);
  const [totalTransaction, setTotalTransaction] = useState(0);
  const [totalNewCustomer, setTotalNewCustomer] = useState({});
  const [totalCustomer, setTotalCustomer] = useState({});
  const [totalReturningCustomer, setTotalReturningCustomer] = useState({});
  const [totalCanceledTransaction, setTotalCanceledTransaction] = useState({});

  const sumAll = (array, field) => {
    let sum = 0;
    array.forEach((item) => {
      sum += item[field] || 0;
    });
    return sum;
  };

  const calculatePercent = (current, previous) => {
    if (previous === 0) {
      return current === 0 ? "0%" : "∞";
    }

    const percent = ((current - previous) / previous) * 100;
    return Number.isInteger(percent) ? `${percent}` : `${percent.toFixed(2)}`;
  };

  useEffect(() => {
    if (data) {
      const currentTotalRevenue =
        sumAll(data?.revenue_daily?.current, "revenue") || 0;
      const currentTotalTransaction =
        sumAll(data?.revenue_daily?.current, "receipts") || 0;
      const prevTotalRevenue =
        sumAll(data?.revenue_daily?.previous, "revenue") || 0;
      const prevTotalTransaction =
        sumAll(data?.revenue_daily?.previous, "receipts") || 0;

      const totalRevenuePercent = calculatePercent(
        currentTotalRevenue,
        prevTotalRevenue
      );
      const totalTransactionPercent = calculatePercent(
        currentTotalTransaction,
        prevTotalTransaction
      );

      const totalRevenueTrend =
        totalRevenuePercent >= 0 || totalRevenuePercent == "∞" ? "up" : "down";
      const totalTransactionTrend =
        totalTransactionPercent >= 0 || totalTransactionPercent == "∞"
          ? "up"
          : "down";

      setTotalCanceledTransaction({
        value: data?.total_voided.current || 0,
        trend: data?.total_voided.trend,
        percent: data?.total_voided.percent || 0,
      });
      setTotalRevenue({
        value: sumAll(data?.revenue_daily.current, "revenue") || 0,
        trend: totalRevenueTrend,
        percent: totalRevenuePercent,
      });
      setTotalTransaction({
        value: sumAll(data?.revenue_daily.current, "receipts") || 0,
        trend: totalTransactionTrend,
        percent: totalTransactionPercent,
      });
      setTotalNewCustomer({
        value: data?.returning_customer_rate?.new_customer_mtd.current,
        trend: data?.returning_customer_rate?.new_customer_mtd.trend,
        percent: data?.returning_customer_rate?.new_customer_mtd.percent,
      });
      setTotalCustomer({
        value: data?.returning_customer_rate?.unique_customers_mtd.current,
        trend: data?.returning_customer_rate?.unique_customers_mtd.trend,
        percent: data?.returning_customer_rate?.unique_customers_mtd.percent,
      });
      setTotalReturningCustomer({
        value: data?.returning_customer_rate?.returning_customers_mtd.current,
        trend: data?.returning_customer_rate?.returning_customers_mtd.trend,
        percent: data?.returning_customer_rate?.returning_customers_mtd.percent,
      });
    }
  }, [data]);

  const formatDateTime = (date) => format(date, "yyyy-MM-dd HH:mm:ss");
  const capToToday = (endDate) => {
    const todayEnd = endOfDay(new Date());
    return endDate > todayEnd ? todayEnd : endDate;
  };
  const MetricCard = ({
    icon,
    iconColor,
    title,
    textColor,
    value = "199999",
    description,
    percent,
    trend,
    prefix,
    reverse = false,
  }) => {
    const isPositive = trend?.toLowerCase() === "up";

    return (
      <>
        {!isMobile && (
          <div className="elevated-card-tight-vertical row-start-start gap-2 w-full !shadow-md min-h-[13rem] h-[13rem] border border-gray-300">
            <div className="col-center-start gap-4 w-full h-full">
              <div className="row-between-center gap-2 w-full">
                <span className="font-bold text-black text-lg">{title}</span>
                <div
                  className={`text-white ${
                    iconColor
                      ? iconColor
                      : "bg-gradient-to-br from-green-400 to-emerald-500"
                  } w-[3rem] h-[3rem] rounded-xl row-center-center`}
                >
                  <i className={`${icon} text-md sm:text-2xl`} />
                </div>
              </div>

              <span className={`text-2xl font-bold ${textColor}`}>
                {prefix}
                <CountUpNumber value={value} />
              </span>
              <div className="col-end-start gap-2 w-full !h-full">
                {percent !== undefined &&
                  percent !== null &&
                  filterValue.dari_tanggal != "2017-12-01 00:00:00" &&
                  filterValue.sampai_tanggal !=
                    formatDateTime(capToToday(endOfDay(new Date()))) && (
                    <div
                      className={`row-start-center gap-2 font-semibold ${
                        (isPositive && !reverse) || (!isPositive && reverse)
                          ? "text-green-500"
                          : "text-red-500"
                      } rounded-lg`}
                    >
                      <i
                        className={`text-sm ${
                          isPositive ? "pi pi-arrow-up" : "pi pi-arrow-down"
                        }`}
                      ></i>
                      <span>{percent}%</span>
                    </div>
                  )}
                {description && (
                  <span className="text-sm text-black row-start-center gap-1">
                    <i className="pi pi-info-circle text-sm"></i>
                    {filterValue.dari_tanggal != "2017-12-01 00:00:00" &&
                    filterValue.sampai_tanggal !=
                      formatDateTime(capToToday(endOfDay(new Date())))
                      ? description
                      : "No trend data"}
                  </span>
                )}
              </div>
            </div>
          </div>
        )}
        {isMobile && (
          <div className="elevated-card-tight-vertical col-between-start gap-2 w-full !p-4 !shadow-md min-h-[16rem] h-[16rem] sm:min-h-[12rem] sm:h-[12rem] border border-gray-300">
            {/* make row wrap on small screens */}
            <div className="flex flex-col justify-center items-start sm:flex-row sm:justify-between sm:items-center gap-4 flex-wrap sm:gap-2 w-full">
              {/* icon never shrinks */}
              <div
                className={`text-white ${
                  iconColor
                    ? iconColor
                    : "bg-gradient-to-br from-green-400 to-emerald-500"
                }
                  w-[3rem] h-[3rem] rounded-xl row-center-center shrink-0`}
              >
                <i className={`${icon} text-2xl`} />
              </div>

              {percent !== undefined &&
                percent !== null &&
                filterValue.dari_tanggal != "2017-12-01 00:00:00" &&
                filterValue.sampai_tanggal !=
                  formatDateTime(capToToday(endOfDay(new Date()))) && (
                  <div
                    className={`row-start-center gap-1 font-semibold ${
                      (isPositive && !reverse) || (!isPositive && reverse)
                        ? "text-green-500"
                        : "text-red-500"
                    } rounded-lg`}
                  >
                    {/* arrow always reflects the real trend */}
                    <i
                      className={`text-xs ${
                        isPositive ? "pi pi-arrow-up" : "pi pi-arrow-down"
                      }`}
                    ></i>
                    <span>{percent}%</span>
                  </div>
                )}
            </div>

            <span
              className={`text-sm sm:text-xl overflow-wrap font-bold ${textColor}`}
            >
              {prefix}
              <CountUpNumber value={value} />
            </span>

            <span className="font-bold text-black text-sm">{title}</span>

            <span className="text-xs text-black row-start-center gap-1">
              <i className="pi pi-info-circle text-sm"></i>
              {filterValue.dari_tanggal != "2017-12-01 00:00:00" &&
              filterValue.sampai_tanggal !=
                formatDateTime(capToToday(endOfDay(new Date())))
                ? description
                : "No trend data"}
            </span>
          </div>
        )}
      </>
    );
  };

  const MetricCardSkeleton = () => (
    <div className="elevated-card-tight-vertical col-start-start gap-4 w-full !shadow-md min-h-[13rem] h-[13rem] border border-gray-300 p-4 overflow-hidden">
      {/* Header row: title placeholder + icon */}
      <div className="row-between-center w-full">
        <Skeleton width="6rem" height="1.5rem" />
        <Skeleton shape="circle" size="3rem" />
      </div>

      {/* Value placeholder */}
      <Skeleton width="5rem" height="2rem" />

      {/* Description or trend section placeholder */}
      <div className="col-center-start gap-2 w-full">
        <Skeleton width="4rem" height="1rem" />
        <Skeleton width="4rem" height="1rem" />
      </div>
    </div>
  );

  const MetricCardSkeletonMobile = () => {
    return (
      <div className="elevated-card-tight-vertical col-between-start gap-2 w-full !p-4 !shadow-md min-h-[16rem] h-[16rem] sm:min-h-[12rem] sm:h-[12rem] border border-gray-300 overflow-hidden">
        {/* Icon + percent row */}
        <div className="flex flex-col justify-center items-start sm:flex-row sm:justify-between sm:items-center gap-4 flex-wrap sm:gap-2 w-full">
          <Skeleton shape="circle" size="3rem" />
          <Skeleton width="4rem" height="1rem" />
        </div>

        {/* Count + label */}
        <Skeleton width="6rem" height="2rem" />
        <Skeleton width="7rem" height="1rem" />

        {/* Description */}
        <Skeleton width="6rem" height="0.75rem" />
      </div>
    );
  };
  return (
    <>
      <AnimatePresence mode="wait">
        {loading ? (
          <motion.div
            key="skeleton"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="w-full"
          >
            {/* your skeleton layout */}
            {!isMobile ? (
              <div className="row-start-center w-full gap-4 h-full">
                <div className="col-center-start gap-4 w-full">
                  <div className="row-between-center w-full gap-4">
                    <MetricCardSkeleton />
                    <MetricCardSkeleton />
                    <MetricCardSkeleton />
                  </div>
                  <div className="row-between-center w-full gap-4">
                    <MetricCardSkeleton />
                    <MetricCardSkeleton />
                    <MetricCardSkeleton />
                  </div>
                </div>
              </div>
            ) : (
              <div className="col-center-start w-full gap-4">
                <div className="row-between-center w-full gap-4">
                  <MetricCardSkeletonMobile />
                  <MetricCardSkeletonMobile />
                </div>
                <div className="row-between-center w-full gap-4">
                  <MetricCardSkeletonMobile />
                  <MetricCardSkeletonMobile />
                </div>
                <div className="row-between-center w-full gap-4">
                  <MetricCardSkeletonMobile />
                  <MetricCardSkeletonMobile />
                </div>
              </div>
            )}
          </motion.div>
        ) : (
          <motion.div
            key="data"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="w-full"
          >
            {/* your data cards JSX */}
            {!isMobile ? (
              <div className="row-start-center w-full gap-4 h-full">
                <div className="col-center-start gap-4 w-full">
                  <div className="row-between-center w-full gap-4">
                    <MetricCard
                      icon="pi pi-dollar"
                      title="Total Revenue"
                      value={totalRevenue.value}
                      description="vs Last Period"
                      trend={totalRevenue.trend}
                      percent={totalRevenue.percent}
                      prefix={"Rp "}
                    />
                    <MetricCard
                      icon="pi pi-shopping-cart"
                      title="Total Transactions"
                      value={totalTransaction.value}
                      description="vs Last Period"
                      trend={totalTransaction.trend}
                      percent={totalTransaction.percent}
                    />
                    <MetricCard
                      icon="pi pi-times"
                      iconColor="from-red-400 to-red-600 bg-gradient-to-br"
                      textColor="text-red-500"
                      title="Voided Transactions"
                      value={totalCanceledTransaction.value}
                      trend={totalCanceledTransaction.trend}
                      description={"vs Last Period"}
                      percent={totalCanceledTransaction.percent}
                      reverse={true}
                    />
                  </div>
                  <div className="row-between-center w-full gap-4">
                    <MetricCard
                      icon="pi pi-users"
                      title="Total Customer"
                      value={totalCustomer.value}
                      description="vs Last Period"
                      trend={totalCustomer.trend}
                      percent={totalCustomer.percent}
                    />
                    <MetricCard
                      icon="pi pi-user"
                      title="Returning Customer"
                      value={totalReturningCustomer.value}
                      description="vs Last Period"
                      trend={totalReturningCustomer.trend}
                      percent={totalReturningCustomer.percent}
                    />
                    <MetricCard
                      icon="pi pi-user-plus"
                      title="New Customer"
                      value={totalNewCustomer.value}
                      description="vs Last Period"
                      trend={totalNewCustomer.trend}
                      percent={totalNewCustomer.percent}
                    />
                  </div>
                </div>
              </div>
            ) : (
              <div className="col-center-start w-full gap-4">
                <div className="row-between-center w-full gap-4">
                  <MetricCard
                    icon="pi pi-dollar"
                    title="Total Revenue"
                    value={totalRevenue.value}
                    description="vs Last Period"
                    trend={totalRevenue.trend}
                    percent={totalRevenue.percent}
                    prefix={"Rp "}
                  />
                  <MetricCard
                    icon="pi pi-users"
                    title="Total Customer"
                    value={totalCustomer.value}
                    description="vs Last Period"
                    trend={totalCustomer.trend}
                    percent={totalCustomer.percent}
                  />
                </div>
                <div className="row-between-center w-full gap-4">
                  <MetricCard
                    icon="pi pi-shopping-cart"
                    title="Total Transactions"
                    value={totalTransaction.value}
                    description="vs Last Period"
                    trend={totalTransaction.trend}
                    percent={totalTransaction.percent}
                  />
                  <MetricCard
                    icon="pi pi-user"
                    title="Returning Customer"
                    value={totalReturningCustomer.value}
                    description="vs Last Period"
                    trend={totalReturningCustomer.trend}
                    percent={totalReturningCustomer.percent}
                  />
                </div>
                <div className="row-between-center w-full gap-4">
                  <MetricCard
                    icon="pi pi-times"
                    iconColor="from-red-400 to-red-600 bg-gradient-to-br"
                    textColor="text-red-500"
                    title="Voided Transactions"
                    value={totalCanceledTransaction.value}
                    trend={totalCanceledTransaction.trend}
                    description={"vs Last Period"}
                    percent={totalCanceledTransaction.percent}
                    reverse={true}
                  />
                  <MetricCard
                    icon="pi pi-user-plus"
                    title="New Customer"
                    value={totalNewCustomer.value}
                    description="vs Last Period"
                    trend={totalNewCustomer.trend}
                    percent={totalNewCustomer.percent}
                  />
                </div>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default OwnerDashboardGeneralOverview;
